// components/navigation/PublicNavigation.tsx
import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Avatar,
  useMediaQuery,
  useTheme,
  Badge,
  Menu,
  MenuItem,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Home as HomeIcon,
  Store as StoreIcon,
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Info as InfoIcon,
  ContactMail as ContactIcon,
  Person as PersonIcon,
  Dashboard as DashboardIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  ShoppingBag as ShoppingBagIcon,
  LocalOffer as LocalOfferIcon,
  LocalShipping as LocalShippingIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useUserRole } from '../../hooks/useUserRole';
import LoginModal from '../auth/LoginModal';
import RegisterModal from '../auth/RegisterModal';

// Styled Components para el header original
const StyledHeader = styled('header')({
  background: 'linear-gradient(135deg, #4b2e12 0%, #5a3a1e 100%)',
  color: '#fff',
  padding: '12px 0',
  position: 'sticky',
  top: 0,
  zIndex: 100,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
});

const StyledNav = styled('nav')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '90%',
  maxWidth: '1400px',
  margin: 'auto',
});

const LogoContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  cursor: 'pointer',
});

const LogoImage = styled('img')({
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  transition: 'transform 0.3s ease',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
  objectFit: 'cover',
  '&:hover': {
    transform: 'scale(1.05) rotate(5deg)',
  },
});

const LogoText = styled('div')({
  '& h3': {
    fontSize: '20px',
    fontWeight: 700,
    margin: 0,
    lineHeight: 1,
    fontFamily: "'Playfair Display', serif",
  },
  '& p': {
    fontSize: '11px',
    letterSpacing: '1px',
    margin: '3px 0 0 0',
    opacity: 0.9,
    fontWeight: 400,
  },
});

const NavLinks = styled('ul')({
  listStyle: 'none',
  display: 'flex',
  gap: '30px',
  padding: 0,
  margin: 0,
  '@media (max-width: 768px)': {
    display: 'none',
  },
});

const NavLink = styled('a')({
  color: '#fff',
  fontWeight: 500,
  fontSize: '15px',
  textDecoration: 'none',
  transition: 'all 0.3s ease',
  position: 'relative',
  paddingBottom: '5px',
  cursor: 'pointer',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: 0,
    height: '2px',
    backgroundColor: '#ffdb9b',
    transition: 'width 0.3s ease',
  },
  '&:hover::after': {
    width: '100%',
  },
  '&:hover': {
    color: '#ffdb9b',
    transform: 'translateY(-2px)',
  },
});

const HeaderRight = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  '@media (max-width: 768px)': {
    gap: '15px',
  },
});

const UserProfile = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  cursor: 'pointer',
  padding: '8px 12px',
  borderRadius: '25px',
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.2)',
    transform: 'translateY(-2px)',
    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)',
  },
  '@media (max-width: 768px)': {
    display: 'none',
  },
});

const UserInfo = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
});

const UserName = styled('span')({
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: 1,
});

const UserEmail = styled('span')({
  fontSize: '11px',
  opacity: 0.8,
  lineHeight: 1,
});

const LoginButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  color: '#fff',
  padding: '10px 20px',
  borderRadius: '25px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 500,
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.2)',
    transform: 'translateY(-2px)',
    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)',
  },
  '@media (max-width: 768px)': {
    display: 'none',
  },
});

const CartContainer = styled('div')({
  position: 'relative',
  cursor: 'pointer',
  transition: 'transform 0.3s ease',
  zIndex: 10,
  '&:hover': {
    transform: 'scale(1.1)',
  },
});

const CartBadge = styled('span')({
  position: 'absolute',
  top: '-8px',
  right: '-8px',
  background: 'linear-gradient(135deg, #ff4444, #cc0000)',
  color: 'white',
  fontSize: '11px',
  fontWeight: 700,
  minWidth: '20px',
  height: '20px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2px',
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
  zIndex: 20,
});

const MobileMenuButton = styled(IconButton)({
  color: '#fff',
  display: 'none',
  padding: '12px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  '@media (max-width: 768px)': {
    display: 'flex',
  },
});

// Drawer styles
const StyledDrawer = styled(Drawer)({
  '& .MuiDrawer-paper': {
    width: 300,
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    boxShadow: '0 0 20px rgba(0,0,0,0.1)',
  },
});

const DrawerHeader = styled(Box)({
  background: 'linear-gradient(135deg, #2e5a33 0%, #4a7c59 100%)',
  color: 'white',
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
});

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  requireAuth?: boolean;
}

const PublicNavigation: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout } = useAuth();
  const { items, updateQuantity, removeFromCart, getTotalItems, getTotalPrice, clearCart } = useCart();
  
  // Obtener el rol del usuario de Django
  const { isAdmin, loading: roleLoading } = useUserRole(user);
  
  // Estado para controlar cuando el admin status ha sido verificado
  const [adminChecked, setAdminChecked] = useState(false);
  
  useEffect(() => {
    if (!roleLoading) {
      setAdminChecked(isAdmin);
    }
  }, [isAdmin, roleLoading]);

  const publicMenuItems: MenuItem[] = [
    { text: 'Inicio', icon: <HomeIcon />, path: '/' },
    { text: 'Productos', icon: <StoreIcon />, path: '/productos' },
    { text: 'Promociones', icon: <LocalOfferIcon />, path: '/promociones' },
    { text: 'Envíos y Retiro', icon: <LocalShippingIcon />, path: '/envios' },
    { text: 'Acerca de', icon: <InfoIcon />, path: '/acerca' },
    { text: 'Contacto', icon: <ContactIcon />, path: '/contacto' },
    { text: 'Mi Carrito', icon: <ShoppingCartIcon />, path: '/carrito' },
  ];

  const authMenuItems: MenuItem[] = [
    { text: 'Mi Cuenta', icon: <AccountCircleIcon />, path: '/mi-cuenta', requireAuth: true },
    { text: 'Checkout', icon: <ShoppingCartIcon />, path: '/checkout', requireAuth: true },
  ];

  const handleDrawerToggle = () => {
    console.log('Drawer toggle clicked, current state:', drawerOpen);
    setDrawerOpen(!drawerOpen);
  };

  const handleMenuClick = (path: string) => {
    console.log('Menu item clicked:', path);
    navigate(path);
    setDrawerOpen(false);
  };

  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    console.log('User menu clicked');
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleUserMenuClose();
      navigate('/');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const handleDashboard = () => {
    navigate('/dashboard');
    handleUserMenuClose();
  };

  const handleMyAccount = () => {
    navigate('/mi-cuenta');
    handleUserMenuClose();
  };

  const handleCartToggle = () => {
    console.log('Cart toggle clicked');
    setCartDrawerOpen(!cartDrawerOpen);
  };

  const handleCartIncrement = (productId: number, currentQuantity: number) => {
    updateQuantity(productId, currentQuantity + 1);
  };

  const handleCartDecrement = (productId: number, currentQuantity: number) => {
    if (currentQuantity > 1) {
      updateQuantity(productId, currentQuantity - 1);
    }
  };

  const handleCartRemoveItem = (productId: number) => {
    removeFromCart(productId);
  };

  const handleCartCheckout = () => {
    setCartDrawerOpen(false);
    if (user) {
      navigate('/checkout');
    } else {
      setLoginModalOpen(true);
    }
  };

  // Modal handlers
  const handleLoginModalOpen = () => {
    setLoginModalOpen(true);
    setDrawerOpen(false); // Cerrar drawer si está abierto
  };

  const handleLoginModalClose = () => {
    setLoginModalOpen(false);
  };

  const handleRegisterModalOpen = () => {
    setRegisterModalOpen(true);
    setDrawerOpen(false); // Cerrar drawer si está abierto
  };

  const handleRegisterModalClose = () => {
    setRegisterModalOpen(false);
  };

  const handleSwitchToRegister = () => {
    setLoginModalOpen(false);
    setRegisterModalOpen(true);
  };

  const handleSwitchToLogin = () => {
    setRegisterModalOpen(false);
    setLoginModalOpen(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(amount);
  };

  const isActivePath = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const drawerContent = (
    <Box>
      {/* Header del Drawer */}
      <DrawerHeader>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LogoImage
              src="/assets/logos/logo-el-pinon-nuevo.png"
              alt="El Piñón Logo"
            />
            <Typography variant="h6" sx={{ fontFamily: "'Playfair Display', serif", fontSize: '1.1rem' }}>
              EL PIÑÓN
            </Typography>
          </Box>
          <IconButton onClick={handleDrawerToggle} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        
        {/* User Info */}
        {user ? (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Avatar sx={{ width: 50, height: 50, mx: 'auto', mb: 1, bgcolor: 'rgba(255,255,255,0.2)' }}>
              {user.email?.charAt(0).toUpperCase()}
            </Avatar>
            <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
              {user.email}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
              Bienvenido a El Piñón
            </Typography>
          </Box>
        )}
      </DrawerHeader>

      {/* Menu Items */}
      <List>
        {publicMenuItems.map((item) => (
          <ListItem
            key={item.path}
            onClick={() => handleMenuClick(item.path)}
            sx={{
              cursor: 'pointer',
              backgroundColor: isActivePath(item.path) ? 'rgba(46, 90, 51, 0.1)' : 'transparent',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
            }}
          >
            <ListItemIcon sx={{ color: isActivePath(item.path) ? '#2e5a33' : '#666' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.text} 
              sx={{ 
                '& .MuiListItemText-primary': { 
                  fontWeight: isActivePath(item.path) ? 'bold' : 'normal',
                  color: isActivePath(item.path) ? '#2e5a33' : 'inherit'
                }
              }}
            />
          </ListItem>
        ))}
      </List>

      <Divider />

      {user ? (
        <>
          <List>
            {authMenuItems.map((item) => (
              <ListItem
                key={item.path}
                onClick={() => handleMenuClick(item.path)}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: isActivePath(item.path) ? 'rgba(46, 90, 51, 0.1)' : 'transparent',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                }}
              >
                <ListItemIcon sx={{ color: isActivePath(item.path) ? '#2e5a33' : '#666' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  sx={{ 
                    '& .MuiListItemText-primary': { 
                      fontWeight: isActivePath(item.path) ? 'bold' : 'normal',
                      color: isActivePath(item.path) ? '#2e5a33' : 'inherit'
                    }
                  }}
                />
              </ListItem>
            ))}
          </List>
          
          {/* Sección de administración para admins */}
          {adminChecked && (
            <>
              <Divider />
              <List>
                <ListItem
                  onClick={() => handleMenuClick('/dashboard')}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: isActivePath('/dashboard') ? 'rgba(46, 90, 51, 0.1)' : 'transparent',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                  }}
                >
                  <ListItemIcon sx={{ color: isActivePath('/dashboard') ? '#2e5a33' : '#666' }}>
                    <DashboardIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Dashboard Admin"
                    sx={{ 
                      '& .MuiListItemText-primary': { 
                        fontWeight: isActivePath('/dashboard') ? 'bold' : 'normal',
                        color: isActivePath('/dashboard') ? '#2e5a33' : 'inherit'
                      }
                    }}
                  />
                </ListItem>
              </List>
            </>
          )}
          
          <Divider />
          <List>
            <ListItem onClick={handleLogout} sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Cerrar Sesión" />
            </ListItem>
          </List>
        </>
      ) : (
        <List>
          <ListItem onClick={handleLoginModalOpen} sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}>
            <ListItemIcon>
              <LoginIcon />
            </ListItemIcon>
            <ListItemText primary="Iniciar Sesión" />
          </ListItem>
          <ListItem onClick={handleRegisterModalOpen} sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}>
            <ListItemIcon>
              <PersonAddIcon />
            </ListItemIcon>
            <ListItemText primary="Registrarse" />
          </ListItem>
        </List>
      )}
    </Box>
  );

  return (
    <>
      {/* Header principal con diseño original */}
      <StyledHeader>
        <StyledNav>
          {/* Botón hamburguesa solo en móvil */}
          <MobileMenuButton
            edge="start"
            aria-label="menu"
            onClick={handleDrawerToggle}
            sx={{ 
              zIndex: 1000,
              '@media (max-width: 768px)': {
                display: 'flex !important',
              }
            }}
          >
            <MenuIcon sx={{ fontSize: '1.5rem' }} />
          </MobileMenuButton>

          <LogoContainer onClick={() => navigate('/')}>
            <LogoImage
              src="/assets/logos/logo-el-pinon-nuevo.png"
              alt="El Piñón Logo"
            />
            <LogoText>
              <h3>EL PIÑÓN</h3>
              <p>MINIMARKET</p>
            </LogoText>
          </LogoContainer>

          <NavLinks>
            <li><NavLink onClick={() => navigate('/')}>Inicio</NavLink></li>
            <li><NavLink onClick={() => navigate('/productos')}>Productos</NavLink></li>
            <li><NavLink onClick={() => navigate('/acerca')}>Nosotros</NavLink></li>
            <li><NavLink onClick={() => navigate('/contacto')}>Contacto</NavLink></li>
            <li><NavLink onClick={() => navigate('/promociones')}>Promociones</NavLink></li>
            <li><NavLink onClick={() => navigate('/envios')}>Envíos y Retiro</NavLink></li>
          </NavLinks>

          <HeaderRight>
            {user ? (
              <UserProfile onClick={handleUserMenuClick}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: '#2e5a33' }}>
                  {(user.nombre_completo || user.email || 'U').charAt(0).toUpperCase()}
                </Avatar>
                <UserInfo>
                  <UserName>
                    {user.nombre_completo || 'Usuario'}
                  </UserName>
                  <UserEmail>
                    {user.email}
                  </UserEmail>
                </UserInfo>
              </UserProfile>
            ) : (
              <LoginButton onClick={handleLoginModalOpen}>
                <PersonIcon sx={{ width: 20, height: 20 }} />
                Ingresar
              </LoginButton>
            )}

            <CartContainer onClick={handleCartToggle}>
              <ShoppingCartIcon sx={{ width: 32, height: 32, fill: '#fff' }} />
              {getTotalItems() > 0 && (
                <CartBadge>{getTotalItems()}</CartBadge>
              )}
            </CartContainer>
          </HeaderRight>
        </StyledNav>
      </StyledHeader>

      {/* Drawer para móvil */}
      <StyledDrawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Mejor rendimiento en móvil
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: 300,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            boxShadow: '0 0 20px rgba(0,0,0,0.1)',
          },
        }}
      >
        {drawerContent}
      </StyledDrawer>

      {/* Menú de usuario */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          mt: 1,
          '& .MuiPaper-root': {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            minWidth: 200,
          },
        }}
      >
        <MenuItem onClick={handleMyAccount}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Mi Cuenta</ListItemText>
        </MenuItem>
        {isAdmin && (
          <MenuItem onClick={handleDashboard}>
            <ListItemIcon>
              <DashboardIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Dashboard Admin</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Cerrar Sesión</ListItemText>
        </MenuItem>
      </Menu>

      {/* Drawer del carrito */}
      <Drawer
        anchor="right"
        open={cartDrawerOpen}
        onClose={handleCartToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100vw', sm: 400 },
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            boxShadow: '0 0 20px rgba(0,0,0,0.1)',
          },
        }}
      >
        <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header del carrito */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e5a33' }}>
              Tu Carrito ({getTotalItems()})
            </Typography>
            <IconButton onClick={handleCartToggle} sx={{ color: '#2e5a33' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Contenido del carrito */}
          {items.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <ShoppingBagIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Tu carrito está vacío
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                ¡Agrega productos desde nuestro catálogo!
              </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  setCartDrawerOpen(false);
                  navigate('/productos');
                }}
                sx={{
                  background: 'linear-gradient(135deg, #2e5a33 0%, #4a7c59 100%)',
                  borderRadius: 3,
                }}
              >
                Ver Productos
              </Button>
            </Box>
          ) : (
            <>
              {/* Lista de productos */}
              <Box sx={{ flex: 1, overflowY: 'auto', mb: 2 }}>
                {items.map((item) => (
                  <Box
                    key={item.product.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 2,
                      mb: 2,
                      borderRadius: 2,
                      bgcolor: '#f8f9fa',
                      border: '1px solid #e0e0e0',
                    }}
                  >
                    {/* Imagen del producto */}
                    <Box
                      component="img"
                      src={item.product.imagen || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjZTBlMGUwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMCIgZmlsbD0iIzk5OTk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPj88L3RleHQ+PC9zdmc+'}
                      alt={item.product.nombre}
                      sx={{
                        width: 60,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 2,
                        flexShrink: 0,
                      }}
                    />

                    {/* Info del producto */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {item.product.nombre}
                      </Typography>
                      <Typography variant="body2" color="primary.main" sx={{ fontWeight: 700 }}>
                        {formatCurrency(item.product.precio_venta)}
                      </Typography>
                    </Box>

                    {/* Controles de cantidad */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => item.product.id && handleCartDecrement(item.product.id, item.quantity)}
                        sx={{
                          bgcolor: '#2e5a33',
                          color: 'white',
                          width: 28,
                          height: 28,
                          '&:hover': { bgcolor: '#1e3e23' },
                        }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      
                      <Typography sx={{ minWidth: 24, textAlign: 'center', fontWeight: 600 }}>
                        {item.quantity}
                      </Typography>
                      
                      <IconButton
                        size="small"
                        onClick={() => item.product.id && handleCartIncrement(item.product.id, item.quantity)}
                        sx={{
                          bgcolor: '#2e5a33',
                          color: 'white',
                          width: 28,
                          height: 28,
                          '&:hover': { bgcolor: '#1e3e23' },
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                      
                      <IconButton
                        size="small"
                        onClick={() => item.product.id && handleCartRemoveItem(item.product.id)}
                        sx={{ color: 'error.main', ml: 1 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Resumen y checkout */}
              <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2">Subtotal:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatCurrency(getTotalPrice())}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Total:
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e5a33' }}>
                    {formatCurrency(getTotalPrice())}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handleCartCheckout}
                  sx={{
                    background: 'linear-gradient(135deg, #2e5a33 0%, #4a7c59 100%)',
                    borderRadius: 3,
                    py: 1.5,
                    fontWeight: 700,
                    mb: 1,
                  }}
                >
                  {user ? 'Proceder al Checkout' : 'Iniciar Sesión para Comprar'}
                </Button>

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    setCartDrawerOpen(false);
                    navigate('/productos');
                  }}
                  sx={{ borderRadius: 3 }}
                >
                  Continuar Comprando
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Drawer>

      {/* Modales de Login y Register */}
      <LoginModal
        open={loginModalOpen}
        onClose={handleLoginModalClose}
        onSwitchToRegister={handleSwitchToRegister}
      />
      
      <RegisterModal
        open={registerModalOpen}
        onClose={handleRegisterModalClose}
        onSwitchToLogin={handleSwitchToLogin}
      />
    </>
  );
};

export default PublicNavigation;